import { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { Button } from './ui/button';
import { Loader2, Plus, Trash2, ExternalLink, X, FileCheck2 } from 'lucide-react';
import { toast } from 'sonner';
import api from '../services/api';
import { MS_FORM_URL } from './AddHOSupportClaimModal';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const CALC_API = `${API}/order-book-reports/ho-support/calculator`;
const ORANGE = '#ee7203';

const pctDisplay = (v) => (v === null || v === undefined || v === '') ? '' : (v * 100).toFixed(1);
const BLANK_MANUAL_LINE = { product_code: '', description: '', supplier_code: '', group: '', type: 'Other', support_pct_display: '' };

/** "Apply for H/O Support" on a saved calculation — the same job the Order Book's H/O £ window does for an order: tick the
 * calculation lines to claim (each pre-matched to the Supplier Matrix supplier / group with the line's own H/O support %),
 * add manual lines if needed, then add them to New Claims under the customer's account and open the H/O Support form.
 * A claim moves from New Claims to Live Accounts when the Monthly Claimed report is uploaded and shows support for that
 * account + supplier + group. */
export default function HOSupportApplyModal({ isOpen, calcId, onClose, onApplied }) {
  const [loading, setLoading] = useState(false);
  const [info, setInfo] = useState(null);
  const [lineState, setLineState] = useState({}); // index -> { checked, supplier_code, group, type, support_pct_display }
  const [manualLines, setManualLines] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!isOpen || !calcId) return;
    setLoading(true);
    setManualLines([]);
    api.get(`${CALC_API}/${calcId}/claim-info`)
      .then((res) => {
        const data = res.data;
        setInfo(data);
        const initial = {};
        (data.lines || []).forEach((l, i) => {
          initial[i] = {
            checked: !!l.group && !l.already_tracked && Number(l.line_ho_support_pct) > 0,
            supplier_code: l.supplier_code || '',
            group: l.group || '',
            type: 'Other',
            support_pct_display: pctDisplay(l.suggested_support_pct),
          };
        });
        setLineState(initial);
      })
      .catch(() => { setInfo(null); toast.error('Failed to load this calculation'); })
      .finally(() => setLoading(false));
  }, [isOpen, calcId]);

  const updateLine = (i, field, value) => setLineState((prev) => ({ ...prev, [i]: { ...prev[i], [field]: value } }));
  const addManualLine = () => setManualLines((prev) => [...prev, { ...BLANK_MANUAL_LINE }]);
  const updateManualLine = (i, field, value) => setManualLines((prev) => prev.map((l, idx) => (idx === i ? { ...l, [field]: value } : l)));
  const removeManualLine = (i) => setManualLines((prev) => prev.filter((_, idx) => idx !== i));

  const handleSubmit = async () => {
    if (!info?.account_number) { toast.error('This customer has no account number — a claim cannot be referenced to their account'); return; }
    const selected = (info.lines || [])
      .map((l, i) => ({ line: l, state: lineState[i] }))
      .filter((x) => x.state?.checked);
    for (const { line, state } of selected) {
      if (!state.supplier_code || !state.group) {
        toast.error(`Enter a Supplier and Group for ${line.product_code || 'the ticked line'}`);
        return;
      }
    }
    const validManual = manualLines.filter((l) => l.supplier_code && l.group);
    if (selected.length === 0 && validManual.length === 0) {
      toast.error('Tick at least one line, or add a manual line with Supplier + Group');
      return;
    }
    const lines = [
      ...selected.map(({ line, state }) => ({
        supplier_code: state.supplier_code,
        group: state.group,
        type: state.type,
        support_pct: state.support_pct_display === '' ? null : parseFloat(state.support_pct_display) / 100,
        product_code: line.product_code || null,
        description: line.description || null,
      })),
      ...validManual.map((l) => ({
        supplier_code: l.supplier_code,
        group: l.group,
        type: l.type,
        support_pct: l.support_pct_display === '' ? null : parseFloat(l.support_pct_display) / 100,
        product_code: l.product_code || null,
        description: l.description || null,
      })),
    ];

    setSubmitting(true);
    try {
      const res = await api.post(`${CALC_API}/${calcId}/apply-claims`, { lines });
      const added = (res.data?.created || []).length;
      const alreadyLive = (res.data?.skipped || []).length;
      toast.success(`Added ${added} line(s) to New Claims${alreadyLive ? ` — ${alreadyLive} already live, left as they are` : ''}`);
      onApplied?.(res.data?.claims_applied_at);
      onClose();
      window.open(MS_FORM_URL, '_blank');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to add claim lines');
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 flex items-center justify-center bg-black/60 p-3" style={{ zIndex: 95 }} data-testid="ho-calc-apply-overlay">
      <div className="bg-gradient-to-br from-orange-50/40 via-white to-orange-50/20 rounded-3xl shadow-2xl w-full border-2 border-orange-300 flex flex-col overflow-hidden" style={{ maxWidth: 900, maxHeight: '94vh' }}>
        <div className="flex items-center justify-between gap-3 px-5 py-3 border-b border-orange-100 bg-white/60 flex-shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl flex items-center justify-center shadow-sm" style={{ background: 'linear-gradient(135deg, #ee7203 0%, #ff8d2a 100%)' }}>
              <FileCheck2 className="w-4 h-4 text-white" />
            </div>
            <div>
              <h2 className="text-lg font-extrabold text-gray-900 leading-tight">Apply for H/O Support{info?.reference ? ` — ${info.reference}` : ''}</h2>
              <p className="text-[11px] text-gray-500">Adds the ticked lines to New Claims; they go Live once the Monthly Claimed report shows the support</p>
            </div>
          </div>
          <button onClick={onClose} className="bg-red-600 hover:bg-red-700 text-white rounded-full p-2 shadow-md" data-testid="ho-calc-apply-close" title="Close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-4 space-y-4 overflow-y-auto flex-1 min-h-0">
          {loading ? (
            <div className="py-10 flex items-center justify-center"><Loader2 className="w-6 h-6 animate-spin" style={{ color: ORANGE }} /></div>
          ) : !info ? (
            <div className="py-10 text-center text-gray-400 text-sm">Couldn't load this calculation.</div>
          ) : (
            <>
              <div className="text-sm text-gray-700" data-testid="ho-calc-apply-account-summary">
                <span className="font-mono font-semibold">{info.account_number || '—'}</span>
                {info.customer_name && <span className="text-gray-500 ml-2">— {info.customer_name}</span>}
              </div>
              {!info.account_number && (
                <div className="text-xs text-red-700 bg-red-50 border border-red-200 rounded-lg px-3 py-2" data-testid="ho-calc-apply-no-account">
                  This customer has no account number (CRM Customer Code), so a claim can't be referenced to their account. Start the calculation again from the customer's CRM account.
                </div>
              )}

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide block mb-1">
                  Calculation lines ({(info.lines || []).length})
                </label>
                {(info.lines || []).length === 0 ? (
                  <div className="text-xs text-gray-400 border border-gray-200 rounded-lg px-3 py-3">
                    This calculation has no lines — use "Add manual line" below.
                  </div>
                ) : (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-gray-500 uppercase text-[10px]">
                          <th className="text-left py-2 px-2 w-6" />
                          <th className="text-left py-2 px-2">Product</th>
                          <th className="text-left py-2 px-2">Supplier</th>
                          <th className="text-left py-2 px-2">Group</th>
                          <th className="text-left py-2 px-2">Type</th>
                          <th className="text-left py-2 px-2">Support %</th>
                        </tr>
                      </thead>
                      <tbody>
                        {(info.lines || []).map((l, i) => {
                          const st = lineState[i] || {};
                          return (
                            <tr key={i} className="border-t border-gray-100" data-testid={`ho-calc-apply-line-${i}`}>
                              <td className="px-2 py-1.5">
                                <input type="checkbox" checked={!!st.checked} onChange={(e) => updateLine(i, 'checked', e.target.checked)}
                                  className="w-4 h-4 accent-orange-500 cursor-pointer" data-testid={`ho-calc-apply-line-checkbox-${i}`} />
                              </td>
                              <td className="px-2 py-1.5">
                                <div className="font-mono text-gray-700">{l.product_code || '—'}</div>
                                <div className="text-gray-400 truncate max-w-[180px]">{l.description}</div>
                                {l.already_tracked && <div className="text-green-600 text-[10px] font-semibold mt-0.5">Already tracked</div>}
                                {!l.group && <div className="text-amber-600 text-[10px] font-semibold mt-0.5">No Supplier Matrix match — enter supplier + group</div>}
                              </td>
                              <td className="px-2 py-1.5"><input type="text" value={st.supplier_code || ''} onChange={(e) => updateLine(i, 'supplier_code', e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs w-20" /></td>
                              <td className="px-2 py-1.5"><input type="text" value={st.group || ''} onChange={(e) => updateLine(i, 'group', e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs w-16" /></td>
                              <td className="px-2 py-1.5">
                                <select value={st.type || 'Other'} onChange={(e) => updateLine(i, 'type', e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs">
                                  <option value="Other">Other</option>
                                  <option value="PIR">PIR</option>
                                </select>
                              </td>
                              <td className="px-2 py-1.5"><input type="number" step="0.1" value={st.support_pct_display || ''} onChange={(e) => updateLine(i, 'support_pct_display', e.target.value)} placeholder="%" className="border border-gray-300 rounded px-1.5 py-1 text-xs w-16" /></td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Manual lines</label>
                  <button onClick={addManualLine} data-testid="ho-calc-apply-add-manual-line" className="flex items-center gap-1 text-xs font-semibold" style={{ color: ORANGE }}>
                    <Plus className="w-3.5 h-3.5" /> Add manual line
                  </button>
                </div>
                {manualLines.length > 0 && (
                  <div className="border border-gray-200 rounded-lg overflow-hidden">
                    <table className="w-full text-xs">
                      <tbody>
                        {manualLines.map((l, i) => (
                          <tr key={i} className="border-t border-gray-100 first:border-t-0" data-testid={`ho-calc-apply-manual-line-${i}`}>
                            <td className="px-2 py-1.5"><input type="text" value={l.description} onChange={(e) => updateManualLine(i, 'description', e.target.value)} placeholder="Description / product ordered or might order" className="border border-gray-300 rounded px-1.5 py-1 text-xs w-full" /></td>
                            <td className="px-2 py-1.5"><input type="text" value={l.supplier_code} onChange={(e) => updateManualLine(i, 'supplier_code', e.target.value)} placeholder="Supplier" className="border border-gray-300 rounded px-1.5 py-1 text-xs w-20" /></td>
                            <td className="px-2 py-1.5"><input type="text" value={l.group} onChange={(e) => updateManualLine(i, 'group', e.target.value)} placeholder="Group" className="border border-gray-300 rounded px-1.5 py-1 text-xs w-16" /></td>
                            <td className="px-2 py-1.5">
                              <select value={l.type} onChange={(e) => updateManualLine(i, 'type', e.target.value)} className="border border-gray-300 rounded px-1.5 py-1 text-xs">
                                <option value="Other">Other</option>
                                <option value="PIR">PIR</option>
                              </select>
                            </td>
                            <td className="px-2 py-1.5"><input type="number" step="0.1" value={l.support_pct_display} onChange={(e) => updateManualLine(i, 'support_pct_display', e.target.value)} placeholder="%" className="border border-gray-300 rounded px-1.5 py-1 text-xs w-16" /></td>
                            <td className="px-2 py-1.5"><button onClick={() => removeManualLine(i)} className="text-gray-400 hover:text-red-500" data-testid={`ho-calc-apply-remove-manual-line-${i}`}><Trash2 className="w-3.5 h-3.5" /></button></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            </>
          )}
        </div>

        {!loading && info && (
          <div className="flex justify-end gap-2 px-5 py-3 border-t border-orange-100 bg-white/60 flex-shrink-0">
            <Button variant="outline" onClick={onClose} data-testid="ho-calc-apply-cancel-btn">Cancel</Button>
            <Button
              onClick={handleSubmit}
              disabled={submitting || !info.account_number}
              className="text-white"
              style={{ backgroundColor: ORANGE }}
              data-testid="ho-calc-apply-submit-btn"
            >
              {submitting ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <ExternalLink className="w-4 h-4 mr-1.5" />}
              Add to New Claims &amp; Open Form
            </Button>
          </div>
        )}
      </div>
    </div>,
    document.body,
  );
}
